import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';

type EchartsLineType = 'line' | 'dotted' | 'dashed';
export interface EChartsMetric {
    echartsSeriesOption: {
        lineStyle: {
            type: EchartsLineType;
            width: number;
        };
    };
    name: string;
}

/**
 * Options for the metrics getter.
 */
export interface MetricsGetterOptions {
    /** Id of metric. For example: `revenue`. It is used to generate metrics like `ad_{metricId}` or `inapp_{metricId}` */
    metricId: string;
    /** The prefix for the metric name. */
    metricName: string;
    /** Suffix for the metric. */
    metricSuffix?: string;
}

export class EChartsHelper {
    /**
     * This function returns the revenue metrics getter. Used in revenue related charts when you need to show revenue metrics. Generates metrics based on the settings. Example of generated metrics:
     * - `revenue_net_pln`
     * - `inapp_revenue_net_pln`
     * - `ad_ARPU_net_usd`
     */
    public static createRevenueMetricsGetter(
        options: MetricsGetterOptions
    ): (context: IQueryContext) => Record<string, EChartsMetric> {
        const getter = (
            context: IQueryContext
        ): Record<string, EChartsMetric> => {
            let metrics: Record<string, any> = {};
            const netGross =
                context.settings.netGross == 'net' ? 'net_' : 'gross_';
            const nameNetGross =
                context.settings.netGross == 'net' ? ' net' : ' gross';

            if (context.settings.showTotalRevenue) {
                metrics[
                    `${options.metricId}_${netGross}${context.settings.currency}`
                ] = {
                    echartsSeriesOption: {
                        lineStyle: { type: 'line', width: 1 },
                    },
                    name: `${options.metricName}${nameNetGross}`,
                };
            }
            if (context.settings.showInappRevenue) {
                metrics[
                    `inapp_${options.metricId}_${netGross}${context.settings.currency}`
                ] = {
                    echartsSeriesOption: {
                        lineStyle: { type: 'dotted', width: 1 },
                    },
                    name: `Inapp ${options.metricName}${nameNetGross}`,
                };
            }
            if (context.settings.showAdRevenue) {
                metrics[
                    `ad_${options.metricId}_${netGross}${context.settings.currency}`
                ] = {
                    echartsSeriesOption: {
                        lineStyle: { type: 'dashed', width: 1 },
                    },
                    name: `Ad ${options.metricName}${nameNetGross}`,
                };
            }

            if (options.metricSuffix) {
                const metricsWithSuffix: Record<string, EChartsMetric> = {};
                Object.keys(metrics).forEach((key) => {
                    const keyWithSuffix = `${key}_${options.metricSuffix}`;
                    metricsWithSuffix[keyWithSuffix] = metrics[key];
                    // metrics[key].name += options.metricSuffix;
                });
                metrics = metricsWithSuffix;
            }

            return metrics;
        };

        return getter;
    }

    /**
     * Creates a metrics getter for retention metrics.
     * This function generates metrics based on the selected day numbers from the context settings.
     * Uses `QueryContextSettingID.RETENTION_SELECTED_DAY_NO` to get the selected days.
     * @param options
     * @returns A function that takes a query context and returns a record of retention metrics.
     */
    public static createRetentionMetricsGetter(
        options: MetricsGetterOptions
    ): (context: IQueryContext) => Record<string, EChartsMetric> {
        // array for circulating between linetypes if more than three elements in selectedDayNo
        // const LINE_TYPE: EchartsLineType[] = ['line'];

        // const metrics: Record<string, EChartsMetric> = {};

        // const selectedDayNo = (context.settings[
        //     QueryContextSettingID.RETENTION_SELECTED_DAY_NO
        // ] ??= ['1', '3', '7']);

        // selectedDayNo.forEach((d, index) => {
        //     metrics[`${options.metricId}_d${d}`] = {
        //         echartsSeriesOption: {
        //             lineStyle: { type: LINE_TYPE[index], width: 1 },
        //         },
        //         name: `${options.metricName}, D${d}`,
        //     };
        // });

        // return metrics;
        const metricsGetter = this.createMultipleMetricsGetter(
            options,
            (context: IQueryContext) => {
                // get all selected day numbers from settings
                const days = context.settings[
                    QueryContextSettingID.RETENTION_SELECTED_DAY_NO
                ] ?? ['1', '3', '7'];
                return days.map((day) => `d${day}`);
            },
            ['line']
        );

        return metricsGetter;
    }

    /**
     * Creates a metrics getter for ROAS metrics.
     * This function generates metrics based on the selected day numbers from the context settings.
     * Uses `QueryContextSettingID.ROAS_SELECTED_DAY_NO` to get the selected days.
     * @param options Options for the metrics getter.
     * @return A function that takes a query context and returns a record of ROAS metrics.
     */
    public static createRoasMetricsGetter(
        options: MetricsGetterOptions
    ): (context: IQueryContext) => Record<string, EChartsMetric> {
        const metricsGetter = this.createMultipleMetricsGetter(
            options,
            (context: IQueryContext) => {
                // get all selected day numbers from settings
                const days = context.settings[
                    QueryContextSettingID.ROAS_SELECTED_DAY_NO
                ] ?? ['1', '3', '7'];
                return days.map((day) => `d${day}`);
            },
            ['line']
        );

        return metricsGetter;
    }

    /**
     *  Creates a metrics getter for multiple metrics.
     *  This function is used to create a metrics getter for multiple metrics. It generates metrics based on the selected metrics from the context settings.
     * @param options Options for the metrics getter.
     * @param selectedMetricsGetter Getter for selected metrics. It should return an array of metric IDs.
     * @param lineTypes Array of line types to use for the metrics. Default is ['line', 'dotted', 'dashed'].
     * @returns A function that takes a query context and returns a record of metrics.
     */
    public static createMultipleMetricsGetter(
        options: MetricsGetterOptions,
        /** Getter for selected metrics */
        selectedMetricsGetter: (context: IQueryContext) => string[],
        lineTypes: EchartsLineType[] = ['line', 'dotted', 'dashed']
    ): (context: IQueryContext) => Record<string, EChartsMetric> {
        const metricsGetter = (
            context: IQueryContext
        ): Record<string, EChartsMetric> => {
            const metrics: Record<string, EChartsMetric> = {};

            // get all selected metrics from settings
            const selectedMetrics = selectedMetricsGetter(context);

            selectedMetrics.forEach((metricId) => {
                metrics[`${options.metricId}_${metricId}`] = {
                    echartsSeriesOption: {
                        lineStyle: {
                            type: lineTypes[
                                selectedMetrics.indexOf(metricId) %
                                    lineTypes.length
                            ],
                            width: 1,
                        },
                    },
                    name: `${options.metricName} ${metricId}`,
                };
            });

            return metrics;
        };

        return metricsGetter;
    }
}
